import styled from '@emotion/styled'
import { Button, Grid, Icon, Theme, Tooltip } from '@yuno/design-system-web'
import useConnect from '../../hooks/connect.hook'
import { capabilitiesData } from '../../utils/constants'
import { ConnectionDrawerForm } from '../connection-drawer-form'
import { useVerifyPermission } from '@yuno/dashboard-container-mfe'
import { PermissionsAvailable } from '@yuno/dashboard-authentication-mfe'
import { useTranslation } from 'react-i18next'

type ConnectionInfoProps = {
  id: string
  title: string
  category: string
  description: string
  capabilities: Array<string>
  website: string
  img: string
}

type StyledProps = { theme?: Theme }

const Container = styled('div')(() => ({
  padding: '18px',
  border: '1px solid #ECEFF2',
  borderRadius: '4px',
  backgroundColor: 'white',
}))

const ConnectionImage = styled('div')<StyledProps>`
  height: 144px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.palette.grey[2]};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 12px;
  border-radius: 4px;
`

const Title = styled('p')<StyledProps>`
  color: ${({ theme }) => theme.palette.neutral.black};
  font-size: 20px;
  display: inline-block;
  margin: 0px;
`

const GridStyled = styled(Grid)<StyledProps>`
  display: flex;
  align-items: center;
`

const CategoryText = styled('p')<StyledProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: start;
  color: ${({ theme }) => theme.palette.grey[5]};
  font-size: ${({ theme }) => theme.typography.tiny.regular.fontSize};
  font-family: ${({ theme }) => theme.typography.tiny.regular.fontFamily};
  padding-top: 5px;
  padding-bottom: 5px;
  padding-left: 6px;
  padding-right: 6px;
  border: 1px solid ${({ theme }) => theme.palette.grey[2]};
  border-radius: 4px;
  margin: 0px;
`
const Description = styled('p')<StyledProps>`
  color: ${({ theme }) => theme.palette.grey[5]};
  font-size: 14px;
  margin-top: 18px;
  margin-bottom: -2px;
`
const Website = styled('p')<StyledProps>`
  color: ${({ theme }) => theme.palette.grey[5]};
  font-size: 12px;
  display: inline-block;
  font-weight: 400;
  margin: 10px 0px 5px 0px;
`
const WebsiteLink = styled('a')<StyledProps>`
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 12px;
  display: inline-block;
  text-decoration: none;
  font-weight: 400;
`

const Hr = styled('hr')<StyledProps>`
  border: 1px solid ${({ theme }) => theme.palette.grey[2]};
  height: 0px;
  margin: 7px 0px 17px 0px;
`

const ConnectButton = styled(Button)<StyledProps>`
  width: 100%;
  height: 40px;
`

const PaymentsContainer = styled('div')<StyledProps>`
  width: 361px;
  width: fit-content;
  margin: 0px 8px 8px 0px;
  padding: 0px;
  display: flex;
  padding-top: 5px;
  padding-right: 6px;
  padding-bottom: 4px;
  padding-left: 7px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.palette.grey[2]};
  justify-content: center;
  align-items: center;
`

const PaymentsText = styled('p')<StyledProps>`
  color: ${({ theme }) => theme.palette.grey[5]};
  font-size: 12px;
  margin: 0px 0px 0px 7px;
`

const Head = ({ title, category }) => (
  <GridStyled>
    <div style={{ flex: 1 }}>
      <Title>{title}</Title>
    </div>
    <CategoryText>{category}</CategoryText>
  </GridStyled>
)

const Capabilities = ({ capabilities }) => {
  return (
    <>
      <Grid container>
        {capabilities?.map((capability) => {
          return (
            <PaymentsContainer>
              <Icon
                name={capabilitiesData[capability]?.icon}
                size="16"
                weight="fill"
                color="#6400FF"
              />
              <PaymentsText>{capabilitiesData[capability]?.text}</PaymentsText>
            </PaymentsContainer>
          )
        })}
      </Grid>
      <Hr />
    </>
  )
}

export const ConnectionInfo = ({
  id,
  title,
  category,
  description,
  capabilities,
  website,
  img,
}: ConnectionInfoProps) => {
  const { allowed } = useVerifyPermission(
    PermissionsAvailable.MANAGE_CONNECTIONS,
  )

  const {
    openDrawerForm,
    drawerData,
    handleSubmit,
    handleCloseDrawer,
    setProviderId,
    postConnection,
    resetForm,
    paymentMethods,
    validating,
    validAccounts,
    handleChangeCountry,
    postValidateAccountsSuccess,
    handleSubmitConfirm,
    postValidateAccountsError,
    actualAccount,
    handleClickReturn,
    valuesForm,
    integrationTypes,
    providerResponse
  } = useConnect()

  const { t } = useTranslation()

  const handleClick = () => {
    setProviderId(id)
  }

  const areCapabilities = capabilities?.length > 0

  return (
    <Container>
      <ConnectionDrawerForm
        error={postConnection?.error?.response.data.message}
        data={postConnection.data}
        status={postConnection.status}
        handleSubmit={handleSubmit}
        category={drawerData?.category}
        name={drawerData?.name}
        icon={drawerData?.icon}
        description={drawerData?.description}
        fields={drawerData?.fields}
        paymentMethods={paymentMethods}
        open={openDrawerForm}
        providerId={drawerData?.id}
        countries={drawerData?.countries}
        onSecondaryAction={handleCloseDrawer}
        onPrimaryAction={resetForm}
        onClose={handleCloseDrawer}
        loading={postConnection.isLoading}
        validating={validating}
        validAccounts={validAccounts}
        postValidateAccountsSuccess={postValidateAccountsSuccess}
        postValidateAccountsError={postValidateAccountsError}
        handleSubmitConfirm={handleSubmitConfirm}
        actualAccountName={actualAccount?.name}
        handleClickReturn={handleClickReturn}
        valuesForm={valuesForm}
        instructions={drawerData?.instructions}
        setupInstructions={drawerData?.setup_instructions}
        handleChangeCountry={handleChangeCountry}
        testKeyValues={drawerData?.test_parameters}
        integrationTypes={integrationTypes}
        providerResponse={providerResponse.data}
      />
      <ConnectionImage>
        <img width="60px" height="60px" src={img} alt={'connection-logo'} />
      </ConnectionImage>
      <Head title={title} category={category} />
      <Description>{description}</Description>
      <Website>Website:&nbsp;</Website>
      <WebsiteLink target="_blank" href={website}>
        {website}
      </WebsiteLink>
      <Hr />
      {areCapabilities && <Capabilities capabilities={capabilities} />}

      <Tooltip
        disableHoverListener={allowed}
        title={t('noAccess')}
        color="default"
        arrow
      >
        <span>
          {/* @ts-ignore */}
          <ConnectButton
            style={{ minHeight: 40 }}
            onClick={handleClick}
            disabled={!allowed}
          >
            {t('connect')}
          </ConnectButton>
        </span>
      </Tooltip>
    </Container>
  )
}
